import { cn } from '@/lib/cn'
import type { HTMLAttributes } from 'react'

export type BadgeVariant =
  | 'default'
  | 'solid'
  | 'soft'
  | 'outline'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'

interface Props extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant
  withDot?: boolean
}

const variants: Record<BadgeVariant, string> = {
  default: 'bg-muted text-foreground',
  solid: 'bg-primary text-primary-foreground',
  soft: 'bg-accent text-accent-foreground',
  outline: 'border border-border text-foreground',
  success:
    'bg-emerald-50 text-emerald-800 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-900/60',
  warning:
    'bg-amber-50 text-amber-900 ring-1 ring-inset ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-900/60',
  danger:
    'bg-rose-50 text-rose-800 ring-1 ring-inset ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:ring-rose-900/60',
  info: 'bg-sky-50 text-sky-800 ring-1 ring-inset ring-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:ring-sky-900/60',
}

const dotColors: Record<BadgeVariant, string> = {
  default: 'bg-muted-foreground',
  solid: 'bg-primary-foreground',
  soft: 'bg-muted-foreground',
  outline: 'bg-muted-foreground',
  success: 'bg-emerald-500',
  warning: 'bg-amber-500',
  danger: 'bg-rose-500',
  info: 'bg-sky-500',
}

export function Badge({ className, variant = 'default', withDot, children, ...props }: Props) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium',
        variants[variant],
        className,
      )}
      {...props}
    >
      {withDot ? (
        <span
          aria-hidden="true"
          className={cn('h-1.5 w-1.5 shrink-0 rounded-full', dotColors[variant])}
        />
      ) : null}
      {children}
    </span>
  )
}
